import { useState } from 'react';
import clsx from 'clsx';
import { useInboxStore, type SuggestedMatch } from '../../stores/inbox-store';
import { klantSignaalKleur, klantSignaalLabel } from '../../lib/inbox/klant-signaal-label';

/**
 * "Mogelijke matches" uit de klant-resolver (suggested_customer_ids) voor een
 * gesprek zonder gekoppelde klant. Eén klik koppelt (match_signal
 * 'suggestion_accepted', via de revision-CAS). Gedeeld door het
 * Klantinfo-paneel in ThreadDetail en de slide-out.
 */
export function KlantSuggesties({ conversationId, suggestions, compact }: {
  conversationId: string;
  suggestions: SuggestedMatch[];
  compact?: boolean;
}) {
  const linkCustomer = useInboxStore((s) => s.linkCustomerToConversation);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  if (suggestions.length === 0) return null;

  const accepteer = async (s: SuggestedMatch) => {
    setLinkingId(s.id);
    try {
      await linkCustomer({
        conversationId,
        customerId: s.id,
        customerType: s.type,
        customerName: s.name,
        contactId: s.contactId ?? undefined,
        contactName: s.contactName ?? undefined,
        matchSignal: 'suggestion_accepted',
      });
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <div className={compact ? 'mt-3' : 'px-2 py-3'}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
        Mogelijke matches
      </div>
      <div className="space-y-1.5">
        {suggestions.map((s) => {
          const detail = s.email ?? s.phone ?? '';
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => accepteer(s)}
              disabled={linkingId !== null}
              title="Koppel deze klant aan het gesprek"
              className="w-full flex items-center gap-2 p-2 bg-white border border-slate-200 rounded hover:border-indigo-400 hover:bg-indigo-50/30 disabled:opacity-50 transition-colors text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-slate-900 truncate">{s.name}</span>
                  <span className={clsx('text-[10px] uppercase tracking-wide px-1 py-0.5 rounded', klantSignaalKleur(s.signal))}>
                    {klantSignaalLabel(s.signal)}
                  </span>
                  {s.type === 'lead' && (
                    <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-100 text-amber-700">Lead</span>
                  )}
                </div>
                {detail && <div className="text-xs text-slate-500 truncate">{detail}</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
